"use client";
import { modalStyles } from "@areahub/theme/src/foundations/styles/modal";
import {
  chakra,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
} from "@chakra-ui/react";
import { useJotaiDisclosure } from "@ui/utils";
import { useEffect } from "react";
import { atom } from "jotai";
import { RiskTable } from "./risk-table";
export interface FemaRiskRatingModalProps {
  isOpen?: boolean;
}

export const femaRiskRatingModalState = atom(false);

export const FemaRiskRatingModal = (props: FemaRiskRatingModalProps) => {
  const { isOpen, onClose, onOpen } = useJotaiDisclosure(
    femaRiskRatingModalState,
  );
  useEffect(() => {
    if (props?.isOpen) {
      onOpen();
    }
  }, [props?.isOpen]);
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      scrollBehavior="inside"
      motionPreset="slideInBottom"
    >
      <ModalOverlay />
      <ModalContent {...modalStyles.content}>
        <ModalHeader apply="styles.flex.centeredWithText">
          <Text as="h2" fontSize="2xl" fontWeight="bold">
            What is the FEMA Risk Rating?
          </Text>
          <Divider />
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="6">
            <Text>
              AreaHub uses FEMA's National Risk Index Ratings to indicate the
              level of risk associated with a natural hazard. The risk equation
              includes three factors:
            </Text>
            <Stack
              apply="styles.flex.centeredWithText"
              spacing="1"
              fontWeight="semibold"
            >
              <Text>national risk index =</Text>
              <HStack>
                <Text color="orange.600">expected annual loss</Text>
                <Text>x</Text>
                <Text color="green.600">social vulnerability</Text>
                <Text>÷</Text>
                <Text color="purple.500">community resilience</Text>
              </HStack>
            </Stack>
            <Text as="span">
              According to FEMA,{" "}
              <chakra.em>
                "for every score there is a qualitative rating that describes
                the nature of a community&apos;s score in comparison to all
                other communities at the same level, ranging from &apos;Very
                Low&apos; to &apos;Very High&apos;{" "}
              </chakra.em>
              <chakra.a
                apply="styles.mdx.a"
                href="https://www.fema.gov/sites/default/files/documents/fema_national-risk-index_technical-documentation.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                [see p. 38]
              </chakra.a>
              .{" "}
              <chakra.em>
                Because all ratings are relative, there are no specific numeric
                values that determine the rating.”
              </chakra.em>
            </Text>
            <RiskTable />
            <HStack spacing={1}>
              <Text>
                To learn more about FEMA's National Risk Index and Risk Ratings,
                visit
              </Text>
              <chakra.a
                apply="styles.mdx.a"
                href="https://www.fema.gov/sites/default/files/documents/fema_national-risk-index_technical-documentation.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                The National Risk Index's Technical Documentation
              </chakra.a>
            </HStack>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FemaRiskRatingModal;
